import React, { useContext, useEffect, useState } from "react";
import "./styles.scss"; // Create a separate CSS file for styling
import {
  FY_MONTH,
  generateDaysInMonth,
  WEEK_DAYS,
  statusTypes,
  financialYears,
} from "./helper";
import TrainingEnrollmentForm from "../training-enrollment";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import AppContext from "../../utils/context";
import FormGenerator from "../../core/form-generator";
import { formatDate, handleFormValidation } from "../../utils";
import moment from "moment/moment";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { HOSTNAME, REST_URLS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import Loader from "../../core/loader";
import dayjs from "dayjs";
import CustomButtonComponent from "../../core/custom-button";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "10px 20px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const FinancialYearCalendar = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const financialYearStart = currentMonth >= 3 ? currentYear : currentYear - 1;
  const financialYearEnd = financialYearStart + 1;
  const { userDetails = {} } = useContext(AppContext);
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({
    courseStartDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
    courseEndDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
  });
  const [fyYearformData, setFyYearformData] = useState(financialYears[0]);
  const [selectedYear, setSelectedYear] = useState(financialYears[0]?.value);
  const [errors, setErrors] = useState({});
  const [year, setYear] = useState(selectedYear);
  const navigate = useNavigate();
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [selectedTrainingDates, setSelectedTrainingDates] = useState({
    startDate: null,
    endDate: null,
    courseId: "",
    model: "",
  });
  const [scheduleResponse, setScheduleResponse] = useState([]);
  const [UpdateBtn, setUpdateBtn] = useState(false);

  const [trainings, setTrainings] = useState([]);

  const getCourseDesiredForm = () => [
    {
      label: "Model",
      name: "model",
      type: "text",
      required: true,
      value: formData.model || "",
    },
    {
      label: "Course Start Date",
      name: "courseStartDate",
      type: "datePicker",
      required: true,
      value: formData.courseStartDate || "",
      minDate: dayjs(),
    },
    {
      label: "Course End Date",
      name: "courseEndDate",
      type: "datePicker",
      required: true,
      value: formData.courseEndDate || "",
      minDate: dayjs(),
    },
  ];

  const isUserAdmin = (role) => {
    if (role === "ADMIN") {
      return true;
    }
    return false;
  };

  useEffect(() => {
    fyYearformData && setYear(selectedYear);
  }, [fyYearformData]);

  const loadTrainings = () => {
    setLoader(true);
    invokeApi(
      HTTP_METHODS.GET,
      isUserAdmin(userDetails.role)
        ? `${HOSTNAME}${REST_URLS.GET_COURSE_DATES_LIST}?startYear=${financialYearStart}&endYear=${financialYearEnd}`
        : `${HOSTNAME}${
            REST_URLS.GET_TRAINING_DATES_LIST
          }?startYear=${financialYearStart}&endYear=${financialYearStart + 1}`,
      null
    ).then((response) => {
      if (response) {
        setScheduleResponse(response);
        setLoader(false);
        setTrainingList(response);
      }
    });
  };

  const setTrainingList = (responseData) => {
    const trainingList = [];

    responseData.forEach((data) => {
      const offsetMillis = (5 * 60 + 30) * 60 * 1000;
      const startDate = new Date(data?.courseDuration.startDate);
      const endDate = new Date(data?.courseDuration.endDate);
      const startDateObj = new Date(startDate.getTime() - offsetMillis);
      const endDateObj = new Date(endDate.getTime() - offsetMillis);

      if (startDateObj.toDateString() === endDateObj.toDateString()) {
        // Single-day event
        trainingList.push({
          year: startDateObj.getFullYear(),
          month: FY_MONTH[((startDateObj.getMonth() + 9) % 12)],
          day: startDateObj.getDate(),
          type: "single", // Mark it as a single-day event
          title: data.course || "",
          courseId: data.id || "",
          model: data.model,
          status: data?.status?.toLowerCase(),
        });
      } else {
        // Multi-day range
        let currentDate = new Date(startDateObj);
        while (currentDate <= endDateObj) {
          trainingList.push({
            year: currentDate.getFullYear(),
            month: FY_MONTH[((currentDate.getMonth() + 9) % 12)],
            day: currentDate.getDate(),
            type:
              currentDate.toDateString() === startDateObj.toDateString()
                ? "start"
                : currentDate.toDateString() === endDateObj.toDateString()
                ? "end"
                : "range",
            title: data.course || "",
            courseId: data.id || "",
            model: data.model,
            status: data?.status?.toLowerCase(),
          });
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });
    setTrainings([...trainingList]);
  };

  useEffect(() => {
    loadTrainings();
  }, [userDetails]);

  const showMonthCalendar = (monthName) => {
    const url = isUserAdmin(userDetails.role)
      ? "/admin/training/training-calendar-view"
      : "/oem/training/training-calendar-view";
    navigate(url, { state: { monthName, trainingDates: trainings, year } });
  };

  const showEnrollForm = (trainingDay) => {
    const course = scheduleResponse.find(
      (item) => item.id === trainingDay?.courseId || ""
    );
    if (isUserAdmin(userDetails.role) && trainingDay) {
      setUpdateBtn(true);
      setFormData({
        ...formData,
        courseStartDate: course.courseDuration.startDate.split("T")[0],
        courseEndDate: course.courseDuration.endDate.split("T")[0],
        courseId: trainingDay.courseId || "",
        model: trainingDay.model,
      });
    } else if (!isUserAdmin(userDetails.role)) {
      setSelectedTrainingDates({
        startDate: course?.courseDuration?.startDate.split("T")[0] || "",
        endDate: course?.courseDuration?.endDate.split("T")[0] || "",
        courseId: trainingDay?.courseId || "",
      });
      // Show enrollment form for non-admin users
      !["completed", "booked"].includes(trainingDay?.status) &&
        setShowEnrollmentForm(true);
    }
  };

  const handleCloseEnrollment = () => {
    setShowEnrollmentForm(false);
  };

  const submitCourseDuration = (isUpdate) => {
    const isValid = handleFormValidation(
      getCourseDesiredForm(),
      setErrors,
      formData
    );
    let payload = {};
    payload = {
      //course: formData.courseDesired,
      model: formData.model,
      courseDuration: {
        startDate: formatDate(formData.courseStartDate, "YYYY-MM-DD"),
        endDate: formatDate(formData.courseEndDate, "YYYY-MM-DD"),
      },
    };

    if (isValid) {
      isUpdate
        ? invokeApi(
            HTTP_METHODS.PUT,
            `${HOSTNAME}${REST_URLS.GET_COURSE_DATES_LIST}/${formData.courseId}`,
            payload
          )
            .then((response) => {
              if (response.code) {
                toast.error(response.message);
              } else {
                toast.success("Course Updated Successfully");
                loadTrainings();
                setUpdateBtn(false);
                resetForm();
              }
            })
            .catch((err) => {
              toast.error("Course creation failed");
            })
        : invokeApi(
            HTTP_METHODS.POST,
            `${HOSTNAME}${REST_URLS.GET_COURSE_DATES_LIST}`,
            payload
          )
            .then((response) => {
              if (response.code) {
                toast.error(response.message);
              } else {
                toast.success("Course Created Successfully");
                loadTrainings();
              }
            })
            .catch((err) => {
              toast.error("Course creation failed");
            });
    }
  };

  const resetForm = () => {
    setFormData({
      courseStartDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
      courseEndDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
    });
  };

  const getRangeDays = (startDate, endDate) => {
    const rangeDays = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      rangeDays.push(new Date(currentDate)); // Add the day to the range
      currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
    }
    return rangeDays;
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    setFyYearformData(year);
    setYear(year); // Update the `year` state based on the selected value
    loadTrainingsForYear(year); // Trigger function to load trainings for the selected year
  };

  const loadTrainingsForYear = (selectedYear) => {
    const financialYearStart = selectedYear;
    const financialYearEnd = financialYearStart + 1;

    setLoader(true);
    invokeApi(
      HTTP_METHODS.GET,
      isUserAdmin(userDetails.role)
        ? `${HOSTNAME}${REST_URLS.GET_COURSE_DATES_LIST}?startYear=${financialYearStart}&endYear=${financialYearEnd}`
        : `${HOSTNAME}${REST_URLS.GET_TRAINING_DATES_LIST}?startYear=${financialYearStart}&endYear=${financialYearEnd}`,
      null
    ).then((response) => {
      if (response) {
        setScheduleResponse(response);
        setLoader(false);
        setTrainingList(response);
      }
    });
  };

  const renderThreeMonthsListView = () => {
    isUserAdmin(userDetails.role)
      ? navigate("/admin/training/training-month-enrollment-view", {
          state: { trainings: scheduleResponse },
        })
      : navigate("/oem/training/training-month-enrollment-view", {
          state: { trainings: scheduleResponse },
        });
  };

  return (
    <>
      <div className="training-year-container">
        <div className="training-title-container">
          <div>
            <span className="training-title">Training</span>
            <div className="training-schedule">Full year schedule</div>
          </div>
          <div className="training-year">
            {" "}
            FY {year} - {Number(year) + 1}
          </div>
        </div>
        <div className="select-year">
          <FormControl sx={{ m: 1, minWidth: 120, display: "block" }}>
            <InputLabel id="financial-year-label">Select a Year</InputLabel>
            <Select
              labelId="financial-year-label"
              className="select-year-dropdown"
              value={selectedYear}
              onChange={handleYearChange}
              label="Select a Year"
            >
              {financialYears.map((year) => (
                <MenuItem key={year.value} value={year.value}>
                  {year.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <Tooltip title={`View Upcoming Training Sessions for 3 months `}>
          <Button
            className="month-view-btn"
            variant="contained"
            onClick={() => renderThreeMonthsListView()}
          >
            {"Upcoming Training Sessions"}
          </Button>
        </Tooltip>
        <div className="submit-btn">
          <Button
            variant="contained"
            onClick={() => {
              if (isUserAdmin(userDetails.role)) {
                navigate("/admin/schedule-event");
              } else {
                navigate("/oem/schedule-event");
              }
            }}
            color="info"
            sx={{ ml: 5 }}
          >
            Schedule Events
          </Button>
        </div>
      </div>
      {isUserAdmin(userDetails.role) && (
        <div className="training-course">
          <div className="course-form">
            <div className="schedule-title">Schedule Training </div>
            <FormGenerator
              config={getCourseDesiredForm()}
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
            />
          </div>
          <div className="submit-btn">
            <Button
              variant="contained"
              onClick={() => submitCourseDuration(UpdateBtn)}
            >
              {UpdateBtn ? "Update" : "Submit"}
            </Button>
          </div>
        </div>
      )}
      <div className="training-year-calendar-container">
        <div className="fy-calendar-container">
          {FY_MONTH.map((monthName, index) => {
            // Handle year transition for Jan-Mar
            const displayYear = index >= 9 ? year + 1 : year;
            const monthIndex = (index + 3) % 12; // Mapping index to correct month (Apr=3, May=4, ..., Mar=2)
            const days = generateDaysInMonth(displayYear, monthIndex);
            return (
              <div key={monthName} className="month">
                <Tooltip
                  title={`View training details for ${monthName} ${displayYear}`}
                  arrow
                >
                  <div
                    className="month-year"
                    onClick={() => showMonthCalendar(monthName)}
                  >
                    {monthName} {displayYear}
                  </div>
                </Tooltip>
                <div className="days-grid">
                  {WEEK_DAYS.map((day) => (
                    <div key={day} className="day-name">
                      {day}
                    </div>
                  ))}
                  {days.map((day) => {
                    const trainingDay = trainings.find(
                      (t) =>
                        t.year === displayYear &&
                        t.month === monthName &&
                        t.day === day
                    );
                    let className = "month-day"; // Default class if no training on the day

                    if (trainingDay) {
                      // Check if it's a start, end, or range day and apply appropriate class
                      if (trainingDay.type === "single") {
                        className = isUserAdmin(userDetails.role)
                          ? `training-single-day-${trainingDay.status}-admin`
                          : `training-single-day-${trainingDay.status}`;
                      } else if (trainingDay.type === "start") {
                        className = isUserAdmin(userDetails.role)
                          ? `training-start-day-${trainingDay.status}-admin`
                          : `training-start-day-${trainingDay.status}`;
                      } else if (trainingDay.type === "end") {
                        className = isUserAdmin(userDetails.role)
                          ? `training-end-day-${trainingDay.status}-admin`
                          : `training-end-day-${trainingDay.status}`;
                      } else if (trainingDay.type === "range") {
                        className = isUserAdmin(userDetails.role)
                          ? `training-range-day-${trainingDay.status}-admin`
                          : `training-range-day-${trainingDay.status}`;
                      }

                      // Highlight the entire range of training days
                      if (
                        trainingDay.type === "start" ||
                        trainingDay.type === "end"
                      ) {
                        // Find all days between start and end date of the training
                        const relatedTraining = trainings.filter(
                          (t) =>
                            t.year === trainingDay.year &&
                            t.month === trainingDay.month &&
                            t.title === trainingDay.title
                        );

                        // Sort related training by day
                        const sortedDates = relatedTraining.sort(
                          (a, b) =>
                            new Date(a.year, a.month - 1, a.day) -
                            new Date(b.year, b.month - 1, b.day)
                        );

                        // Get the range days
                        const rangeDays = getRangeDays(
                          new Date(
                            sortedDates[0].year,
                            sortedDates[0].month - 1,
                            sortedDates[0].day
                          ),
                          new Date(
                            sortedDates[sortedDates.length - 1].year,
                            sortedDates[sortedDates.length - 1].month - 1,
                            sortedDates[sortedDates.length - 1].day
                          )
                        );

                        // Apply the highlight class to all range days
                        if (rangeDays.some((date) => date.getDate() === day)) {
                          className = isUserAdmin(userDetails.role)
                            ? "training-range-day-admin"
                            : "training-range-day"; // Apply the range highlight
                        }
                      }
                    }

                    return (
                      <div
                        key={day}
                        className={className}
                        onClick={() => showEnrollForm(trainingDay)} // Pass the trainingDay object
                      >
                        {day}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="training-status-legend">
          {statusTypes.map((data) => (
            <div className={`legend-${data.toLowerCase()}`}>{data}</div>
          ))}
        </div>
      </div>

      {showEnrollmentForm && (
        <div>
          <BootstrapDialog
            onClose={() => handleCloseEnrollment()}
            aria-labelledby="customized-dialog-title"
            open={showEnrollmentForm}
            fullWidth
            maxWidth="lg"
          >
            <DialogTitle
              sx={{
                m: 0,
                p: 2,
                "&.MuiDialogTitle-root": { "font-weight": "600" },
              }}
              id="customized-dialog-title"
            >
              TRAINING ENROLLMENT
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => handleCloseEnrollment()}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent
              dividers
              sx={{ "&.MuiDialogContent-dividers": { border: 0 } }}
            >
              <TrainingEnrollmentForm
                startDate={selectedTrainingDates.startDate}
                endDate={selectedTrainingDates.endDate}
                courseId={selectedTrainingDates.courseId}
              />
            </DialogContent>
          </BootstrapDialog>
        </div>
      )}

      {loader && <Loader />}
    </>
  );
};

export default FinancialYearCalendar;
